import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import SEO from '../components/SEO';
import { BreadcrumbStructuredData, ServiceStructuredData } from '../components/StructuredData';

interface ServiceItem {
  title: string;
  detail: string;
}

interface ProofItem {
  value: string;
  label: string;
  detail: string;
  href: string;
}

interface GuideItem {
  title: string;
  description: string;
  href: string;
}

export interface ServicePageProps {
  title: string;
  eyebrow: string;
  description: string;
  canonicalPath: string;
  intro: string;
  fit: string[];
  services: ServiceItem[];
  process: ServiceItem[];
  proof: ProofItem[];
  guides: GuideItem[];
}

const bookingUrl = 'https://cal.com/linustse/meeting-with-linus?overlayCalendar=true';

export default function ServicePage({
  title,
  eyebrow,
  description,
  canonicalPath,
  intro,
  fit,
  services,
  process,
  proof,
  guides,
}: ServicePageProps) {
  const url = `https://www.greyfielddata.com${canonicalPath}`;

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      <SEO title={title} description={description} url={url} />
      <BreadcrumbStructuredData
        items={[
          { name: 'Home', url: 'https://www.greyfielddata.com' },
          { name: title, url },
        ]}
      />
      <ServiceStructuredData name={title} description={description} url={url} />
      <Navigation />

      <main>
        <section className="relative overflow-hidden bg-[#0a1628] pb-24 pt-36 md:pb-28 md:pt-48">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:56px_56px]" />
          <div className="absolute -right-32 top-0 h-[32rem] w-[32rem] rounded-full bg-blue-500/15 blur-3xl" />
          <div className="relative mx-auto max-w-7xl px-6">
            <nav aria-label="Breadcrumb" className="mb-10 flex items-center gap-2 text-sm text-gray-400">
              <Link to="/" className="hover:text-white">Home</Link>
              <span aria-hidden="true">/</span>
              <span className="text-gray-200">{title}</span>
            </nav>
            <p className="mb-5 text-sm font-semibold uppercase tracking-[0.18em] text-blue-300">{eyebrow}</p>
            <h1 className="max-w-4xl text-5xl font-semibold leading-[1.08] tracking-tight text-white md:text-7xl">{title}</h1>
            <p className="mt-8 max-w-3xl text-xl leading-relaxed text-gray-300 md:text-2xl">{intro}</p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-xl bg-white px-7 py-4 font-semibold text-[#0a1628] transition hover:bg-gray-100">
                Discuss your data problem
              </a>
              <a href="#work" className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-4 font-medium text-white transition hover:bg-white/10">
                See relevant work
              </a>
            </div>
          </div>
        </section>

        <section className="border-b border-gray-100 bg-gray-50 py-16">
          <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#1a4e94]">Good fit</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-gray-900">When this work is worth doing</h2>
            </div>
            <ul className="grid gap-4 sm:grid-cols-2">
              {fit.map((item) => (
                <li key={item} className="rounded-2xl border border-gray-200 bg-white p-5 leading-relaxed text-gray-700">{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-24 md:py-28">
          <div className="mx-auto max-w-7xl px-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-[#1a4e94]">What we do</p>
            <h2 className="mt-3 max-w-3xl text-4xl font-semibold leading-[1.1] tracking-tight text-gray-900 md:text-5xl">Models, pipelines, reports, and operating notes</h2>
            <div className="mt-12 grid gap-5 md:grid-cols-2">
              {services.map((item) => (
                <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-7 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                  <p className="mt-3 leading-relaxed text-gray-600">{item.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#0a1628] py-24 text-white md:py-28">
          <div className="mx-auto max-w-7xl px-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-blue-300">How we work</p>
            <h2 className="mt-3 max-w-3xl text-4xl font-semibold leading-[1.1] tracking-tight md:text-5xl">Start with the decision, then trace the data</h2>
            <div className="mt-12 grid gap-5 md:grid-cols-3">
              {process.map((item, index) => (
                <article key={item.title} className="rounded-2xl border border-white/10 bg-white/5 p-7">
                  <span className="text-sm font-semibold text-blue-300">0{index + 1}</span>
                  <h3 className="mt-5 text-xl font-semibold">{item.title}</h3>
                  <p className="mt-3 leading-relaxed text-gray-300">{item.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="work" className="scroll-mt-24 bg-gray-50 py-24 md:py-28">
          <div className="mx-auto max-w-7xl px-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-[#1a4e94]">Relevant work</p>
            <h2 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-gray-900 md:text-5xl">What the work changed</h2>
            <div className="mt-12 grid gap-6 md:grid-cols-2">
              {proof.map((item) => (
                <Link key={item.label} to={item.href} className="group rounded-3xl border border-gray-200 bg-white p-8 transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg">
                  <p className="text-4xl font-semibold text-[#1a4e94]">{item.value}</p>
                  <h3 className="mt-4 text-xl font-semibold text-gray-900 group-hover:text-[#1a4e94]">{item.label}</h3>
                  <p className="mt-3 leading-relaxed text-gray-600">{item.detail}</p>
                  <span className="mt-6 inline-flex font-semibold text-[#1a4e94]">Read the case study →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 md:py-28">
          <div className="mx-auto max-w-7xl px-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-[#1a4e94]">Guides</p>
            <h2 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-gray-900 md:text-5xl">Setup and troubleshooting guides</h2>
            <div className="mt-12 grid gap-5 md:grid-cols-2">
              {guides.map((guide) => (
                <Link key={guide.href} to={guide.href} className="rounded-2xl border border-gray-200 p-7 transition hover:border-blue-200 hover:shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900">{guide.title}</h3>
                  <p className="mt-3 leading-relaxed text-gray-600">{guide.description}</p>
                  <span className="mt-5 inline-flex font-semibold text-[#1a4e94]">Read the guide →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-50 px-6 py-24">
          <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-[radial-gradient(circle_at_top_left,_#173f78_0,_#0a192f_44%,_#071525_100%)] p-10 text-center text-white ring-1 ring-inset ring-white/10 md:p-16">
            <div className="absolute left-0 top-0 h-80 w-80 rounded-full bg-blue-400/10 blur-3xl" />
            <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:40px_40px]" />
            <div className="relative z-10">
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">A practical first step</p>
              <h2 className="text-3xl font-semibold leading-[1.1] tracking-tight md:text-5xl">Start with the problem</h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">You don't need a finished requirements document. Start with a number the team doesn't trust, a report that takes too long, or a warehouse bill that keeps climbing.</p>
              <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="group mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-7 py-4 font-semibold text-[#1a4e94] shadow-xl shadow-black/15 transition hover:bg-slate-100">
                Discuss your data problem
                <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">→</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-100 bg-white py-12">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-6 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
          <p>Greyfield Data | Snowflake and Sigma consulting</p>
          <div className="flex flex-wrap gap-5">
            <Link to="/case-studies" className="hover:text-gray-900">Case studies</Link>
            <Link to="/blog" className="hover:text-gray-900">Articles</Link>
            <Link to="/privacy" className="hover:text-gray-900">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
